import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';
import SEO from '@/components/SEO';

export const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Privacy Policy"
        description="Learn how BMI Calc Hub collects, uses, and protects your personal data. Your privacy matters to us."
        canonical="/privacy"
      />
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Calculator
          </Button>
        </Link>

        <Card className="card-elevated">
          <CardContent className="p-8 sm:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Privacy Policy</h1>
                <p className="text-sm text-muted-foreground">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">
              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">1. Introduction</h2>
                <p>
                  Welcome to BMI Calc Hub ("we," "our," or "us"). We are committed to protecting your privacy and ensuring 
                  the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard 
                  your data when you use our BMI calculator website at bmicalchub.com.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">2.1 Information You Provide</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Information:</strong> If you create an account, we collect your name, email address, and password.</li>
                  <li><strong>Health Data:</strong> BMI calculations including height, weight, age, gender, and waist measurements that you voluntarily enter.</li>
                  <li><strong>Communications:</strong> Any information you provide when contacting us for support.</li>
                </ul>

                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">2.2 Information Collected Automatically</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers.</li>
                  <li><strong>Usage Data:</strong> Pages visited, features used, and time spent on the site.</li>
                  <li><strong>Cookies:</strong> Small data files stored on your device (see our Cookie Policy for details).</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">3. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide and improve our BMI calculator services</li>
                  <li>Store your calculation history (if you have an account)</li>
                  <li>Send important updates about our services</li>
                  <li>Respond to your inquiries and support requests</li>
                  <li>Analyze usage patterns to improve user experience</li>
                  <li>Ensure security and prevent fraud</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">4. Data Storage and Security</h2>
                
                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">4.1 Local Storage</h3>
                <p>
                  For users without accounts, BMI calculations are stored locally in your browser using localStorage. 
                  This data never leaves your device and is not accessible to us.
                </p>

                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">4.2 Cloud Storage</h3>
                <p>
                  For registered users, your data is securely stored in our database. We use industry-standard 
                  encryption and security measures to protect your information.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">5. Data Sharing</h2>
                <p>
                  We do <strong>not</strong> sell, trade, or rent your personal information to third parties. 
                  We may share data only in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>With your explicit consent</li>
                  <li>To comply with legal obligations</li>
                  <li>To protect our rights and prevent fraud</li>
                  <li>With service providers who assist in operating our website (under strict confidentiality agreements)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">6. Your Rights</h2>
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                  <li><strong>Portability:</strong> Export your data in a common format</li>
                  <li><strong>Withdraw Consent:</strong> Opt out of optional data processing</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">7. Children's Privacy</h2>
                <p>
                  Our service is not intended for children under 13 years of age. We do not knowingly collect 
                  personal information from children under 13. If you believe we have collected such information, 
                  please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">8. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any significant 
                  changes by posting the new policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">9. Contact Us</h2>
                <p>
                  If you have questions about this Privacy Policy or your personal data, please contact us at:
                </p>
                <p className="mt-2">
                  <strong>Email:</strong> info@bmicalchub.com<br />
                  <strong>Website:</strong> bmicalchub.com
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
